(() => {
  const storageKey = 'orvon_wishlist_v1';
  const measurementId = 'G-EELCL2MK4D';
  const analyticsQueue = [];
  let analyticsPermission;
  let privacyLoading = false;
  let googleTagConfigured = false;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    localStorage.setItem(storageKey, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('orvon:wishlist-change', { detail: { items } }));
  };

  const updateCount = (items = read()) => {
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });
  };

  const updateButtons = (items = read()) => {
    const ids = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const active = ids.has(String(button.dataset.productId));
      button.setAttribute('aria-pressed', String(active));
      const label = active ? button.dataset.removeLabel : button.dataset.addLabel;
      if (label) button.setAttribute('aria-label', label);
    });
  };

  const flushAnalyticsQueue = () => {
    if (analyticsPermission !== true) {
      analyticsQueue.length = 0;
      return;
    }
    while (analyticsQueue.length) {
      const callback = analyticsQueue.shift();
      callback();
    }
  };

  const setAnalyticsPermission = (allowed) => {
    analyticsPermission = allowed;
    flushAnalyticsQueue();
  };

  const loadPrivacyPermission = () => {
    if (privacyLoading || analyticsPermission !== undefined) return;
    const shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', loadPrivacyPermission, { once: true });
      } else {
        setAnalyticsPermission(true);
      }
      return;
    }
    privacyLoading = true;
    shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        privacyLoading = false;
        const privacy = window.Shopify?.customerPrivacy;
        if (error || typeof privacy?.analyticsProcessingAllowed !== 'function') {
          setAnalyticsPermission(true);
          return;
        }
        setAnalyticsPermission(privacy.analyticsProcessingAllowed());
      }
    );
  };

  const runWhenAnalyticsAllowed = (callback) => {
    if (analyticsPermission === true) {
      callback();
      return;
    }
    if (analyticsPermission === false) return;
    analyticsQueue.push(callback);
    loadPrivacyPermission();
  };

  const ensureGoogleTag = () => {
    if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    const tagSelector = `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`;
    if (!document.querySelector(tagSelector) && !document.querySelector('[data-orvon-wishlist-ga4]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.dataset.orvonWishlistGa4 = 'true';
      document.head.appendChild(script);
    }
    if (!googleTagConfigured) {
      window.gtag('config', measurementId, { send_page_view: false });
      googleTagConfigured = true;
    }
  };

  const publishAnalytics = (eventName, item) => {
    runWhenAnalyticsAllowed(() => {
      ensureGoogleTag();
      const price = Number(item.price || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      window.gtag('event', eventName, {
        send_to: measurementId,
        currency,
        value: price,
        product_id: String(item.id),
        product_name: item.title,
        product_handle: item.handle,
        product_brand: item.vendor,
        product_url: item.url,
        items: [
          {
            item_id: String(item.id),
            item_name: item.title,
            item_brand: item.vendor,
            price,
            quantity: 1
          }
        ]
      });
    });
  };

  const itemFromButton = (button) => ({
    id: button.dataset.productId,
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle,
    vendor: button.dataset.productVendor,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    price: Number(button.dataset.productPrice || 0)
  });

  document.addEventListener('visitorConsentCollected', (event) => {
    const eventPermission = event.detail?.analyticsAllowed;
    if (typeof eventPermission === 'boolean') {
      setAnalyticsPermission(eventPermission);
      return;
    }
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed === 'function') {
      setAnalyticsPermission(privacy.analyticsProcessingAllowed());
    }
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-button]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const item = itemFromButton(button);
    const items = read();
    const index = items.findIndex((saved) => String(saved.id) === String(item.id));
    const adding = index === -1;
    if (adding) items.push(item);
    else items.splice(index, 1);
    write(items);
    updateCount(items);
    updateButtons(items);
    publishAnalytics(adding ? 'add_to_wishlist' : 'remove_from_wishlist', item);
  });

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD'
  }).format(Number(cents || 0) / 100);

  const renderWishlist = () => {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;
    const items = read();
    const empty = root.querySelector('[data-wishlist-empty]');
    const grid = root.querySelector('[data-wishlist-grid]');
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    grid.innerHTML = '';
    items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'orvon-wishlist-card';
      const image = item.image ? '<img src="' + item.image + '" alt="" loading="lazy" width="720" height="720">' : '';
      const vendor = item.vendor ? '<p class="orvon-wishlist-card__vendor">' + item.vendor + '</p>' : '';
      card.innerHTML =
        '<a class="orvon-wishlist-card__image" href="' + item.url + '">' + image + '</a>' +
        '<div class="orvon-wishlist-card__body">' + vendor +
        '<h2><a href="' + item.url + '">' + item.title + '</a></h2>' +
        '<p class="orvon-wishlist-card__price">' + money(item.price) + '</p>' +
        '<button type="button" data-wishlist-button data-product-id="' + item.id + '" data-product-handle="' + item.handle + '" data-product-title="' + item.title + '" data-product-vendor="' + (item.vendor || '') + '" data-product-url="' + item.url + '" data-product-image="' + (item.image || '') + '" data-product-price="' + item.price + '">' + root.dataset.removeLabel + '</button></div>';
      grid.appendChild(card);
    });
    updateButtons(items);
  };

  document.addEventListener('orvon:wishlist-change', renderWishlist);
  document.addEventListener('DOMContentLoaded', () => {
    loadPrivacyPermission();
    updateCount();
    updateButtons();
    renderWishlist();
  });
  document.addEventListener('shopify:section:load', () => {
    updateCount();
    updateButtons();
    renderWishlist();
  });

  loadPrivacyPermission();
})();
